/**
 * Sign in, sign up, and the way back in when neither works.
 *
 * One screen with three modes rather than three screens: the fields are almost
 * the same, and a player who guessed wrong should be one click from the right
 * one rather than navigating back. "Forgot" is a mode too, and deliberately
 * reachable from the sign-in form itself — the moment somebody needs it is the
 * moment they are least willing to go hunting.
 *
 * The server is the authority on what is acceptable. The checks here only
 * decide whether the button is worth pressing — they never decide the answer,
 * and the message the player sees on failure is the server's, not an invented
 * local guess at what went wrong.
 */

import { useState } from 'react';

export type AuthMode = 'login' | 'register' | 'forgot';

export function Auth({
  busy,
  error,
  notice,
  onLogin,
  onRegister,
  onForgot,
}: {
  busy: boolean;
  error: string | null;
  notice: string | null;
  onLogin: (email: string, password: string) => void;
  onRegister: (email: string, password: string, displayName: string) => void;
  onForgot: (email: string) => void;
}) {
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');

  const emailLooksRight = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  // Signing in takes a name or an address. Registering and resetting need a real
  // address: one is where the recovery link goes, the other is the link.
  const identifierOk = mode === 'login' ? email.trim().length >= 3 : emailLooksRight;
  const passwordLongEnough = password.length >= 8;
  const nameLongEnough = displayName.trim().length >= 3;
  const ready =
    !busy &&
    identifierOk &&
    (mode === 'forgot' || passwordLongEnough) &&
    (mode !== 'register' || nameLongEnough);

  /**
   * Why the button is not going to do anything.
   *
   * A disabled button that gives no reason is the worst control in an
   * interface: the person presses it, nothing happens, and they conclude the
   * site is broken. Which is exactly what happened here.
   */
  const blocker =
    email.trim().length === 0
      ? null
      : !identifierOk
        ? mode === 'login'
          ? 'نام یا ایمیل‌تان را وارد کنید.'
          : 'آدرس ایمیل معتبر نیست.'
        : mode !== 'forgot' && password.length > 0 && !passwordLongEnough
          ? 'رمز باید دست‌کم ۸ کاراکتر باشد.'
          : mode === 'register' && displayName.trim().length > 0 && !nameLongEnough
            ? 'نام نمایشی باید دست‌کم ۳ کاراکتر باشد.'
            : null;

  const submitLabel =
    mode === 'login' ? 'Sign In' : mode === 'register' ? 'Create Account' : 'Send Reset Link';

  return (
    <div className="join">
      <form
        className="join-card"
        onSubmit={(event) => {
          event.preventDefault();
          if (!ready) return;
          if (mode === 'login') onLogin(email.trim(), password);
          else if (mode === 'register') onRegister(email.trim(), password, displayName.trim());
          else onForgot(email.trim());
        }}
      >
        <div className="mark">♠</div>
        <h1>Iran Poker Tour</h1>
        <p>نسخه‌ی آزمایشی — پول مجازی</p>

        <div className="mode-switch" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'login'}
            className={mode === 'login' ? 'on' : ''}
            onClick={() => setMode('login')}
          >
            Sign In
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'register'}
            className={mode === 'register' ? 'on' : ''}
            onClick={() => setMode('register')}
          >
            Sign Up
          </button>
        </div>

        <label htmlFor="email">{mode === 'login' ? 'نام یا ایمیل' : 'ایمیل'}</label>
        <input
          id="email"
          // Not type="email" when a name is allowed: a phone would refuse the
          // field and show the wrong keyboard for half of what it accepts.
          type={mode === 'login' ? 'text' : 'email'}
          inputMode={mode === 'login' ? 'text' : 'email'}
          className="lat"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete={mode === 'login' ? 'username' : 'email'}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
        />

        {mode === 'register' && (
          <>
            <label htmlFor="name">نام نمایشی</label>
            <input
              id="name"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              maxLength={24}
              autoComplete="nickname"
            />
            <span className="field-note">۳ تا ۲۴ کاراکتر. سر میز همین را می‌بینند.</span>
          </>
        )}

        {mode !== 'forgot' && (
          <>
            <label htmlFor="password">رمز</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            />
            {mode === 'register' && <span className="field-note">دست‌کم ۸ کاراکتر.</span>}
          </>
        )}

        {mode === 'forgot' && (
          <span className="field-note">
            لینک ساختن رمز تازه به این آدرس فرستاده می‌شود. لینک یک ساعت معتبر است.
          </span>
        )}

        {error !== null && <div className="error">{error}</div>}
        {notice !== null && <div className="notice">{notice}</div>}
        {error === null && blocker !== null && <div className="blocker">{blocker}</div>}

        <button className="btn-check" type="submit" disabled={!ready}>
          {busy ? '…' : submitLabel}
        </button>

        {mode === 'login' && (
          <button type="button" className="link-button" onClick={() => setMode('forgot')}>
            رمزتان را فراموش کرده‌اید؟
          </button>
        )}
        {mode === 'forgot' && (
          <button type="button" className="link-button" onClick={() => setMode('login')}>
            بازگشت به ورود
          </button>
        )}
        {mode === 'register' && (
          <p className="field-note">با ساخت حساب، ۲۰۰۰ چیپ مجازی برای شروع می‌گیرید.</p>
        )}
      </form>
    </div>
  );
}

/**
 * The screen a reset link lands on.
 *
 * It stands apart from `Auth` because it is not a choice between modes — the
 * player arrived here holding a token, and the only thing to do is pick a
 * password. The token itself never appears on screen and is stripped from the
 * address bar before this renders, so a screenshot or a shared link cannot
 * carry it.
 */
export function ResetPassword({
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  busy: boolean;
  error: string | null;
  onSubmit: (password: string) => void;
  onCancel: () => void;
}) {
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');

  const longEnough = password.length >= 8;
  const matches = password === again;
  const ready = longEnough && matches && !busy;

  return (
    <div className="join">
      <form
        className="join-card"
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) onSubmit(password);
        }}
      >
        <div className="mark">♠</div>
        <h1>رمز تازه</h1>
        <p>یک رمز تازه برای حسابتان انتخاب کنید.</p>

        <label htmlFor="new-password">رمز تازه</label>
        <input
          id="new-password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
        />
        <span className="field-note">دست‌کم ۸ کاراکتر.</span>

        <label htmlFor="again">تکرار رمز</label>
        <input
          id="again"
          type="password"
          value={again}
          onChange={(event) => setAgain(event.target.value)}
          autoComplete="new-password"
        />
        {again.length > 0 && !matches && <span className="field-note">دو رمز یکی نیستند.</span>}

        {error !== null && <div className="error">{error}</div>}

        <button className="btn-check" type="submit" disabled={!ready}>
          {busy ? '…' : 'Set Password'}
        </button>
        <button type="button" className="link-button" onClick={onCancel}>
          بی‌خیال، برگرد به ورود
        </button>
      </form>
    </div>
  );
}
